import {useMemo} from 'react'
import {
  QueryStatus,
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query'

import {TransformedFileList, CASBFilesAPIParams} from 'interfaces'
import {CASBService} from 'services/api'
import {getTableData} from './transformer'
import {removeEmptyKeysFromObjects} from 'utils'

interface CASBFilesQueryResponseType {
  fetchNextPage: () => Promise<UseInfiniteQueryResult>
  hasNextPage: boolean
  status: QueryStatus
  files?: TransformedFileList[]
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  error: unknown
}

interface CustomInfiniteQueryOptions {
  platform?: string
  filters?: CASBFilesAPIParams
}

export const CASB_FILES_QUERY_KEY = 'getCASBFiles'

export const useCASBFilesQuery = ({
  platform,
  filters,
}: CustomInfiniteQueryOptions): CASBFilesQueryResponseType => {
  const {data, fetchNextPage, status, isLoading, isSuccess, isError, error} =
    useInfiniteQuery({
      queryKey: [CASB_FILES_QUERY_KEY, platform, filters],
      queryFn: ({pageParam}) => {
        const cleanedFilters = removeEmptyKeysFromObjects(
          filters,
        ) as CASBFilesAPIParams

        // Explicitly preserve hasPersonalEmailShare=false
        if (filters?.hasPersonalEmailShare === false) {
          cleanedFilters.hasPersonalEmailShare = false
        }

        return CASBService.getCASBFiles({
          platform,
          params: {
            after: pageParam,
            ...cleanedFilters,
          },
        })
      },
      getNextPageParam: lastPage => lastPage?.data?.data?.pageInfo?.endCursor,
      /**
       * This is set to false because we always want to prioritize the data coming from the live-api
       * over files API and don't want the files data to be refreshed.
       */
      refetchOnWindowFocus: false,
    })

  const hasNextPage = useMemo(() => {
    const lastPage = data?.pages?.at(-1)
    return Boolean(lastPage?.data?.data?.pageInfo?.hasNextPage)
  }, [data?.pages])

  // Deduplicate files based on unique fileId
  const rawFilesData = useMemo(() => {
    const allFiles = data?.pages?.flatMap(page => page?.data?.data?.files) || []
    const fileMap = new Map()
    allFiles.forEach(file => {
      fileMap.set(file.fileSummary.uniqueId, file)
    })
    return Array.from(fileMap.values())
  }, [data?.pages])

  const files = rawFilesData ? getTableData(rawFilesData) : []

  return {
    fetchNextPage,
    hasNextPage,
    status,
    files,
    isLoading,
    isSuccess,
    isError,
    error,
  }
}
